import { useToasts } from '@brunommsilva/react-foundations';
import {
  Box,
  Button,
  Card,
  CardHeader,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TEST_IDS } from '../../config/test-ids';
import useCreateAccount from '../../hooks/accounts/use-create-account';
import { AccountType } from '../../models/accounts';
import AccountTypeDropdown from './account-type-dropdown';

interface State {
  name: string;
  type?: AccountType;
}

const INITIAL_STATE: State = {
  name: '',
  type: undefined,
};

interface CreateAccountFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  isDialog?: boolean;
}

const CreateAccountForm = ({
  onSuccess,
  onCancel,
  isDialog = false,
}: CreateAccountFormProps) => {
  const [state, setState] = useState<State>(INITIAL_STATE);
  const { t } = useTranslation();
  const { showToast } = useToasts();
  const {
    createAccount,
    isCreating,
    wasCreatedWithSuccess,
    wasCreatedWithoutSuccess,
  } = useCreateAccount();

  useEffect(() => {
    if (wasCreatedWithSuccess) {
      showToast({
        title: t('CREATE_ACCOUNT_SUCCESS_TOAST.TITLE'),
        message: t('CREATE_ACCOUNT_SUCCESS_TOAST.MESSAGE'),
      });
      if (onSuccess) {
        onSuccess();
      }
    }
    if (wasCreatedWithoutSuccess) {
      showToast({
        title: t('CREATE_ACCOUNT_ERROR_TOAST.TITLE'),
        message: t('CREATE_ACCOUNT_ERROR_TOAST.MESSAGE'),
        type: 'error',
      });
    }
  }, [
    wasCreatedWithSuccess,
    wasCreatedWithoutSuccess,
    onSuccess,
    t,
    showToast,
  ]);

  const isFormValid = () => {
    return state.name && state.type;
  };

  const onCreateAccount = () => {
    if (!isFormValid()) {
      return;
    }

    createAccount({
      name: state.name,
      type: state.type!,
    });
  };

  const renderContent = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: isDialog ? 1 : 0,
        }}
      >
        <TextField
          name="ACCOUNT_NAME"
          label={t('ACCOUNT_NAME')}
          placeholder={t('ACCOUNT_NAME')}
          fullWidth
          value={state.name}
          onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, name: change.target.value });
          }}
          slotProps={{
            htmlInput: { 'data-testid': TEST_IDS.accounts.nameInput },
          }}
        />
        <Box
          data-testid={TEST_IDS.accounts.typeDropdown}
          sx={{ display: 'contents' }}
        >
          <AccountTypeDropdown
            selected={
              state.type
                ? { id: state.type, name: t(`ACCOUNT_TYPE_${state.type}`) }
                : undefined
            }
            onSelected={(type) =>
              setState({ ...state, type: type?.id as AccountType })
            }
          />
        </Box>
      </Box>
    );
  };

  const renderCreateButton = () => {
    return (
      <Button
        variant="contained"
        data-testid={TEST_IDS.accounts.createButton}
        disabled={isCreating || !isFormValid()}
        onClick={onCreateAccount}
      >
        {t('CREATE')}
      </Button>
    );
  };

  const renderFooter = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {isDialog && onCancel && (
          <Button
            variant="outlined"
            color="inherit"
            disabled={isCreating}
            onClick={onCancel}
          >
            {t('CANCEL')}
          </Button>
        )}
        {renderCreateButton()}
      </Box>
    );
  };

  if (isDialog) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {renderContent()}
        {renderFooter()}
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('CREATE_NEW_ACCOUNT')}
          </Typography>
        }
      />
      <Box sx={{ p: 2, pt: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {renderContent()}
        </Box>
      </Box>
      <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
        {renderFooter()}
      </Box>
    </Card>
  );
};

export default CreateAccountForm;
